import {
    Flex,
    Heading,
    HStack,
    Link,
    Text,
    useChakraContext,
    useMediaQuery,
    useSlotRecipe,
} from '@chakra-ui/react';

import isNil from 'lodash/isNil';

import { usePluralisationTranslation } from '@nocowanie/common-ui/hooks';
import { IconsLinear } from '@nocowanie/icons/linear';

import { FeaturedImageWrapper } from '@app/components/molecules/featured-image-wrapper';

import { FeaturedCountryItemProps } from './featured-country-item.props';

export const FeaturedCountryItem = ({
    name,
    accommodationsCount,
    image,
    path,
    cities,
    isHighlighted = false,
    ...rest
}: FeaturedCountryItemProps) => {
    const pluralizeTranslation = usePluralisationTranslation('common', 'accommodationLabels');
    const theme = useChakraContext();
    const xl = theme.breakpoints.values.find(b => b.name === 'xl')?.min;
    const [isLargerThanXl] = useMediaQuery([`(min-width: ${xl})`], {
        fallback: [false],
    });
    const isWideImg = isHighlighted && isLargerThanXl;
    const recipe = useSlotRecipe({ key: 'featuredCountryItem' });
    const styles = recipe();

    if (![name, accommodationsCount, image, path, cities].every(item => !isNil(item))) return;

    const accommodationsLabel = pluralizeTranslation(accommodationsCount, 'accommodation');

    return (
        <FeaturedImageWrapper
            imgUrl={image}
            href={path}
            isWiderWidth={isWideImg}
            alt={name}
            data-testid="featured-country-item-container"
            {...rest}
        >
            <HStack alignItems={'baseline'}>
                <Heading as={'h3'} size={'lg'} data-testid="featured-country-item-header">
                    {name}
                </Heading>
                <IconsLinear.Flash
                    alignSelf={'center'}
                    color={'selectiveYellow'}
                    fill={'selectiveYellow'}
                    size={'xs'}
                    ml={{
                        base: 1,
                        md: 2,
                    }}
                />
                <Text as="span">{accommodationsLabel}</Text>
            </HStack>
            <Flex css={styles.links} data-testid="featured-country-item-links">
                {cities.map((city, index) => (
                    <Link
                        key={index}
                        href={city.path}
                        data-testid="featured-country-item-link"
                        variant="plain"
                        color={'white'}
                    >
                        {city.name}
                    </Link>
                ))}
            </Flex>
        </FeaturedImageWrapper>
    );
};
